import type { SessionStatus, SentimentLevel, ContactStatus } from "@type/agent";
import {
    ThumbsUp,
    Minus,
    ThumbsDown,
    Shuffle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export function getScoreColor(score: number): string {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
}

export function getScoreLabel(score: number): string {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Poor";
}

export function formatDuration(seconds: number): string {
    // Guard against NaN/undefined/negative inputs from BE aggregates.
    if (!Number.isFinite(seconds) || seconds < 0) return "0s";
    // Round to whole seconds first — `seconds % 60` on a fractional value
    // (e.g. 60.1) leaks floating-point noise like 0.09999999999999...
    const total = Math.round(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
}

export function getSentimentBgColor(sentiment: SentimentLevel): string {
    switch (sentiment) {
        case "positive":
            return "bg-green-50 text-green-700";
        case "neutral":
            return "bg-gray-50 text-gray-700";
        case "negative":
            return "bg-red-50 text-red-700";
        case "mixed":
            return "bg-amber-50 text-amber-700";
    }
}

export function getSentimentLabel(sentiment: SentimentLevel): string {
    return sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
}

export function getSentimentIcon(sentiment: SentimentLevel): LucideIcon {
    switch (sentiment) {
        case "positive":
            return ThumbsUp;
        case "neutral":
            return Minus;
        case "negative":
            return ThumbsDown;
        case "mixed":
            return Shuffle;
    }
}

export function getScoreBgColor(score: number): string {
    if (score >= 80) return "bg-green-50 text-green-700";
    if (score >= 60) return "bg-amber-50 text-amber-700";
    if (score >= 40) return "bg-orange-50 text-orange-700";
    return "bg-red-50 text-red-700";
}

export const statusBadgeConfig: Record<
    SessionStatus,
    {
        label: string;
        variant: "primary" | "success" | "warning" | "error" | "default";
    }
> = {
    pending: { label: "Pending", variant: "default" },
    scheduled: { label: "Scheduled", variant: "default" },
    active: { label: "In Progress", variant: "primary" },
    analyzing: { label: "Analyzing", variant: "warning" },
    results_ready: { label: "Completed", variant: "success" },
    failed: { label: "Failed", variant: "error" },
};

export const sentimentChipConfig: Record<
    SentimentLevel,
    { label: string; variant: "success" | "default" | "error" | "warning" }
> = {
    positive: { label: "Positive", variant: "success" },
    neutral: { label: "Neutral", variant: "default" },
    negative: { label: "Negative", variant: "error" },
    mixed: { label: "Mixed", variant: "warning" },
};

export const chipVariantStyles: Record<string, string> = {
    primary: "bg-blue-50 text-blue-600 border-blue-200",
    success: "bg-green-50 text-green-700 border-green-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    error: "bg-red-50 text-red-700 border-red-200",
    default: "bg-gray-100 text-gray-600 border-gray-200",
};

export const chipDotColor: Record<string, string> = {
    primary: "bg-blue-500",
    success: "bg-green-500",
    warning: "bg-amber-500",
    error: "bg-red-500",
    default: "bg-gray-400",
};

export const contactStatusBadgeConfig: Record<
    ContactStatus,
    {
        label: string;
        variant: "primary" | "success" | "warning" | "error" | "default";
        animated?: boolean;
    }
> = {
    pending: { label: "Pending", variant: "default" },
    queued: { label: "Queued", variant: "default" },
    dialing: { label: "Dialing", variant: "primary", animated: true },
    ringing: { label: "Ringing", variant: "primary", animated: true },
    in_progress: { label: "In Progress", variant: "primary", animated: true },
    called: { label: "Called", variant: "success" },
    completed: { label: "Completed", variant: "success" },
    failed: { label: "Failed", variant: "error" },
    dnc: { label: "Do Not Call", variant: "error" },
};

// Stale/zombie session detection — sessions stuck in non-terminal state
// with no activity for 30+ minutes are treated as effectively terminal
export const STALE_SESSION_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

export function isSessionEffectivelyTerminal(session: {
    status: string;
    messageCount?: number;
    duration: number;
    startedAt: Date;
}): boolean {
    if (session.status === "results_ready" || session.status === "failed") return true;
    // Stale zombie: active/pending with no activity for 30+ min
    if (
        ["active", "pending"].includes(session.status) &&
        (session.messageCount ?? 0) === 0 &&
        session.duration === 0
    ) {
        const age = Date.now() - new Date(session.startedAt).getTime();
        if (age > STALE_SESSION_THRESHOLD_MS) return true;
    }
    // Stale analyzing: stuck in analyzing for 30+ min
    if (session.status === "analyzing") {
        const age = Date.now() - new Date(session.startedAt).getTime();
        if (age > STALE_SESSION_THRESHOLD_MS) return true;
    }
    return false;
}
